import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaFileExport } from "react-icons/fa";
import { MdExpandMore } from "react-icons/md";
import {
  fetchMetadata,
  fetchProblems,
  fetchReport,
  fetchSections,
  getComments,
  getParticipants,
  GetReportFile,
  getReportFiles,
  updateReportName,
} from "../../api/reportsApi";
import { fetchStudy } from "../../api/studyApi";
import { getTemplateProblems } from "../../api/questionsApi";
import { ProblemsSection } from "../../components/problemsSection/ProblemsSection";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchReportActions } from "../../api/reportsApi";
import { ProblemType, ReviewComment, TemplateType, ReportType } from "../../types/Rows";
import { ReportHeader } from "./ReportHeader";
import { SectionsList } from "./section/SectionsList";

import ReactPDF, {
  usePDF,
  Document as PDFDoc,
  Page as PDFPage,
  Text as PDFText,
  View as PDFView,
} from "@react-pdf/renderer";
import { PDFDocument, degrees } from "pdf-lib";
import {
  NavigationBadge,
  useDiscussion,
  useNavigationBadgesStore,
  useReport,
} from "../..";
import ReportToolbar from "../../components/toolbar/ReportToolbar";
import { toast } from "react-toastify";
import { failureSaveToast } from "../../helpers/successToast";
import { useUserContext } from "../user/authProvider";
import { DocumentGenerator } from "./document/documentGenerator";
import { ReportDocumentV2 } from "./document/v2/ReportDocumentV2";
import { VisitTitleEditor } from "./VisitTitleEditor";
import type { PdfDesign } from "../../components/pdfDesign/PdfDesign";
import { FinalComment } from "./metadata/finalComment";
import { MetadataEditModal } from "./metadata/MetadataEditModal";
import { ParticipantsSectionsList } from "./participantsSection";
import { ReportFiles } from "./section/ReportFiles";
import { ActionLogGrid } from "./actionLog/ActionLogGrid";
import { ReportSiteIssuesSection } from "../siteIssues/ReportSiteIssuesSection";
import { SiteIssuesInReport } from "../../types/configs";
import { ReportStateTimeline } from "./ReportStateTimeline";
import { ReportTrackingDates } from "./ReportTrackingDates";
import { CmTrackingDates } from "./CmTrackingDates";
import { Button } from "../../components/ui/button";
import { isMultiDayVisit } from "../../utils/visitDate";

// SR-011: tytuł załącznika jako pasek nad treścią, nie osobna strona.
// Pasek ma szerokość A4; na stronach o innym rozmiarze jest skalowany do szerokości strony.
const TITLE_BAR = { width: 595, height: 30 };

const AttachmentTitleBar = ({ index, name }: { index: number; name: string }) => (
  <PDFDoc>
    <PDFPage
      size={[TITLE_BAR.width, TITLE_BAR.height]}
      style={{
        fontFamily: "Roboto",
        paddingHorizontal: 24,
        justifyContent: "center",
        backgroundColor: "#F7F7F5",
        borderBottomWidth: 1,
        borderBottomColor: "#EBEBEA",
      }}
    >
      <PDFView style={{ flexDirection: "row" }}>
        <PDFText style={{ fontSize: 9, color: "gray", marginRight: 6 }}>
          ZAŁĄCZNIK {index}:
        </PDFText>
        <PDFText style={{ fontSize: 9, fontWeight: "semibold", flex: 1 }}>{name}</PDFText>
      </PDFView>
    </PDFPage>
  </PDFDoc>
);

// Pierwsza strona załącznika: pasek z tytułem u góry, treść pomniejszona tak, żeby zmieściła się pod nim.
// Rozmiar strony się nie zmienia, kolejne strony załącznika są kopiowane bez zmian.
const withTitleBar = async (content: PDFDocument, index: number, name: string) => {
  const barBytes = await ReactPDF.pdf(<AttachmentTitleBar index={index} name={name} />)
    .toBlob()
    .then((b) => b.arrayBuffer());

  const out = await PDFDocument.create();
  const [bar] = await out.embedPdf(barBytes, [0]);
  const [first] = await out.embedPdf(content, [0]);

  // Strony ze skanów często mają /Rotate; embedPdf go pomija, więc obrót odtwarzamy przy rysowaniu
  const rotation = ((content.getPage(0).getRotation().angle % 360) + 360) % 360;
  const swapped = rotation === 90 || rotation === 270;
  const width = swapped ? first.height : first.width;
  const height = swapped ? first.width : first.height;
  const barHeight = TITLE_BAR.height * (width / TITLE_BAR.width);
  const scale = (height - barHeight) / height;

  // pole na treść pod paskiem (lewy dolny róg x0, y0)
  const x0 = (width - width * scale) / 2;
  const y0 = 0;
  const w = first.width * scale;
  const h = first.height * scale;
  const placement =
    rotation === 90 ? { x: x0, y: y0 + w, rotate: degrees(-90) } :
    rotation === 180 ? { x: x0 + w, y: y0 + h, rotate: degrees(180) } :
    rotation === 270 ? { x: x0 + h, y: y0, rotate: degrees(90) } :
    { x: x0, y: y0 };

  const page = out.addPage([width, height]);
  page.drawPage(first, { ...placement, width: w, height: h });
  page.drawPage(bar, { x: 0, y: height - barHeight, width, height: barHeight });

  const rest = content.getPageIndices().slice(1);
  const copied = await out.copyPages(content, rest);
  copied.forEach((p) => out.addPage(p));
  return out;
};

function ReportPage() {
  let { reportId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { parsedName } = useUserContext();
  const { update: updateReport } = useReport();
  const { update: updateDiscussion } = useDiscussion();
  const [isMetadataModalOpen, setIsMetadataModalOpen] = useState<boolean>(false);
  const [isMetadataExpanded, setIsMetadataExpanded] = useState<boolean>(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState("");
  const nameInputRef = useRef<HTMLInputElement>(null);
  // Fetch ReportActions here and pass to ReportActionsSection
  // kategorie do kolumny „Czego dotyczy” w PDF (ten sam klucz co w tabeli rejestru, więc z cache)
  const { data: problemsDictionary } = useQuery(["problemsDictionary"], getTemplateProblems, { staleTime: 10 * 60 * 1000 });
  const { data: actions, isLoading: actionsLoading, error: actionsError } = useQuery(
    [reportId, "ReportActions"],
    () => fetchReportActions(reportId)
  );

  const { data, error } = useQuery(
    [reportId, "Report"],
    () => fetchReport(reportId!),
    {
      enabled: reportId != undefined,
    }
  );

  const { data: metadata } = useQuery(
    [reportId, "Metadata"],
    () => fetchMetadata(reportId!),
    {
      enabled: reportId != undefined,
    }
  );

  const { data: problems } = useQuery(
    [reportId, "Problems"],
    () => fetchProblems(reportId!),
    {
      enabled: reportId != undefined,
      initialData: [],
    }
  );

  const { data: sections } = useQuery(
    [reportId, "Sections"],
    () => fetchSections(reportId!),
    {
      enabled: reportId != undefined,
      initialData: [],
    }
  );
  const { data: participants } = useQuery(
    [reportId, "Participants"],
    async () => getParticipants(reportId),
    { enabled: reportId != null, initialData: [] }
  );

  const { data: reportFiles } = useQuery(
    [reportId, "Files"],
    async () => getReportFiles(reportId),
    { enabled: reportId != null, initialData: [] }
  );

  const { data: studyData } = useQuery(
    [data?.studyId, "Study"],
    () => fetchStudy(data!.studyId),
    { enabled: data?.reportType === ReportType.CENTRAL_MONITORING && !!data?.studyId }
  );

  const { mutate: saveName } = useMutation(
    (newName: string) => updateReportName(reportId!, newName),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([reportId, "Report"]);
        setIsEditingName(false);
      },
    }
  );

  const startEditingName = (currentName: string) => {
    setEditedName(currentName);
    setIsEditingName(true);
    setTimeout(() => nameInputRef.current?.select(), 0);
  };

  const commitName = () => {
    const trimmed = editedName.trim();
    if (trimmed && data && trimmed !== data.name) {
      saveName(trimmed);
    } else {
      setIsEditingName(false);
    }
  };

  const setItems = useNavigationBadgesStore((state: any) => state.setItems);

  useEffect(() => {
    if (data) {
      updateReport(data);
      setItems([
        { text: "Badania", url: "/" } as NavigationBadge,
        {
          text: data.study,
          url: "/study/" + data.studyId,
          // links: allStudies?.map((link) => ({
          //   name: link.name,
          //   value: "/study/" + link.id,
          // })),
        } as NavigationBadge,
        {
          text: data.name,
          url: "/reports/" + data.id,
          // links: studies?.resorts?.flatMap((resort) =>
          //   resort?.reports?.flatMap((report) => ({
          //     name: resort.name + " " + report.name,
          //     value: "/reports/" + report.id,
          //   }))
          // ),
        } as NavigationBadge,
      ]);
    }
  }, [data, setItems]);

  useQuery(["Discussion"], async () => {
    var res = await getComments(reportId);
    var map = new Map<string, ReviewComment[]>();
    res.data.forEach((item) => {
      map.set(item.parentId, [...(map.get(item.parentId) ?? []), item]);
    });
    updateDiscussion(map);
    return map;
  });

  const MyDoc = ({
    types,
    isFul,
    design = "v1",
  }: {
    types: TemplateType[];
    isFul: boolean;
    design?: PdfDesign;
  }) => {
    let header: string;
    if (data.reportType === ReportType.CENTRAL_MONITORING) {
      const fmt = (d?: Date) =>
        d ? new Date(d).toLocaleDateString("pl-PL") : "";
      const periodStart = fmt(metadata?.visitDate);
      const periodEnd = fmt(metadata?.visitDateEnd);
      const period =
        periodStart && periodEnd
          ? ` za okres ${periodStart}-${periodEnd}`
          : periodStart
          ? ` za okres ${periodStart}`
          : "";
      const base = data.name;
      const insertIdx = base.indexOf(" w badaniu ");
      header =
        insertIdx >= 0
          ? base.slice(0, insertIdx) + period + base.slice(insertIdx)
          : base + period;
    } else {
      header = (isFul ? "Follow up letter" : "Raport") + " z " + data.name;
    }
    const cmResorts = studyData?.resorts?.map((r) => r.name) ?? [];

    // Nowy wygląd (v2 / v2 uproszczony) tylko dla raportów z wizyty; CM bez projektu, więc zostaje v1
    const useV2 = design !== "v1" && data.reportType !== ReportType.CENTRAL_MONITORING;
    const Doc = () => useV2 ? (
      <ReportDocumentV2
        variant={design === "v2" ? "color" : "simple"}
        reportName="Raport z wizyty monitorującej"
        visitLabel={data.name}
        resortName={data.resort}
        address={data.address}
        metadata={metadata}
        problemAnswers={problems}
        sections={sections}
        participants={participants}
        types={types}
        footer={isFul ? data.footerFul : data?.footer}
        actions={actions}
        author={parsedName}
        composedDate={data.toVerifyDate ?? new Date()}
        categories={problemsDictionary?.categories}
      />
    ) : (
      <DocumentGenerator
        reportName={header}
        resortName={data.resort}
        address={data.address}
        metadata={metadata}
        problemAnswers={problems}
        sections={sections}
        participants={participants}
        types={types}
        footer={isFul ? data.footerFul : data?.footer}
        actions={actions}
        author={parsedName}
        composedDate={data.toVerifyDate ?? new Date()}
        reportType={data.reportType}
        visitLabel={data.reportType === ReportType.CENTRAL_MONITORING ? undefined : data.name}
        cmResorts={cmResorts}
        categories={problemsDictionary?.categories}
      />
    );
    const [instance, updateInstance] = usePDF({
      document: <Doc />,
    });

    useEffect(() => {
      if (instance) updateInstance(<Doc />);
    }, [types, isFul, design]);

    if (instance.loading) return <div>Loading ...</div>;

    return (
      <div
        onClick={async () => {
          try {
            const includeAttachments = types.includes(TemplateType.ATTACHMENTS);
            const attachmentSegments: PDFDocument[] = [];
            const skipped: string[] = [];
            if (includeAttachments) {
              let index = 0;
              for (const item of reportFiles ?? []) {
                try {
                  const f = await GetReportFile(item.id, item.id);
                  const ext = item.name.split(".").pop()?.toLowerCase();
                  let content: PDFDocument;
                  if (ext === "jpg" || ext === "jpeg" || ext === "png") {
                    const imgPdf = await PDFDocument.create();
                    const img = ext === "png"
                      ? await imgPdf.embedPng(f)
                      : await imgPdf.embedJpg(f);
                    const A4 = { width: 595, height: 842 };
                    const scale = Math.min(A4.width / img.width, A4.height / img.height);
                    const w = img.width * scale;
                    const h = img.height * scale;
                    const page = imgPdf.addPage([A4.width, A4.height]);
                    page.drawImage(img, { x: (A4.width - w) / 2, y: (A4.height - h) / 2, width: w, height: h });
                    content = imgPdf;
                  } else {
                    content = await PDFDocument.load(f);
                  }
                  index += 1;
                  attachmentSegments.push(await withTitleBar(content, index, item.name));
                } catch {
                  skipped.push(item.name);
                }
              }
              if (skipped.length > 0) {
                toast.warn(
                  `Pominięto ${skipped.length} plik${skipped.length === 1 ? "" : "i"} (nieobsługiwany format — obsługiwane: PDF, JPG, PNG):\n${skipped.join(", ")}`,
                  { autoClose: false }
                );
              }
            }

            const mainDocBuffer = await ReactPDF.pdf(<Doc />).toBlob().then((b) => b.arrayBuffer());
            const mainDocPdf = await PDFDocument.load(mainDocBuffer);

            const mergedPdf = await PDFDocument.create();
            for (const pdf of [mainDocPdf, ...attachmentSegments]) {
              const pages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
              pages.forEach((page) => mergedPdf.addPage(page));
            }

            const pdfBytes: any = await mergedPdf.save();
            const url = URL.createObjectURL(new Blob([pdfBytes], { type: "application/pdf" }));
            window.open(url, "_blank");
          } catch (error) {
            const msg = error instanceof Error ? error.message : String(error);
            toast.error(`Błąd generowania PDF: ${msg}`, { autoClose: false });
          }
        }}
      >
        {"Eksport"}{" "}
        <FaFileExport style={{ display: "inline" }} className="m-1" />
      </div>
    );
  };

  return (
    <>
      {data ? (
        <div className="flex flex-col h-[calc(100vh-56px)]">
          {/* Breadcrumb */}
          <div className="bg-[var(--sc-surface)] border-b border-[var(--sc-border)] h-10 px-10 flex items-center gap-1.5 flex-shrink-0">
            <button
              onClick={() => navigate("/")}
              className="text-[12px] text-[var(--sc-muted)] hover:text-[var(--sc-text)] bg-transparent border-none cursor-pointer p-0 transition-colors"
            >
              Badania
            </button>
            <span className="text-[12px] text-[var(--sc-muted)]">›</span>
            {data.studyId && (
              <>
                <button
                  onClick={() => navigate(`/study/${data.studyId}`)}
                  className="text-[12px] text-[var(--sc-muted)] hover:text-[var(--sc-text)] bg-transparent border-none cursor-pointer p-0 transition-colors"
                >
                  {data.study}
                </button>
                <span className="text-[12px] text-[var(--sc-muted)]">›</span>
              </>
            )}
            <span className="text-[12px] font-medium text-[var(--sc-text)]">
              {data.name}
            </span>
          </div>

          {/* Main area */}
          <div className="flex flex-1 overflow-hidden">
            {/* Left sidebar */}
            <aside className="w-[280px] bg-[var(--sc-surface)] border-r border-[var(--sc-border)] flex flex-col flex-shrink-0 overflow-hidden">
              <ReportToolbar
                resortName={data.resort}
                state={data.state}
                reportId={data.id}
                MyDoc={MyDoc}
                studyId={data.studyId}
                templateType={data.templateType}
              />
              {metadata && (
                <ReportHeader
                  name={data.name}
                  address={data.address}
                  resortName={data.resort}
                  created={data.created}
                  state={data.state}
                  sections={sections ?? []}
                  metadata={metadata}
                  onOpenMetadataModal={() => setIsMetadataModalOpen(true)}
                />
              )}
            </aside>

            {/* Main content */}
            <main className="flex-1 overflow-y-auto px-8 py-6 bg-[var(--sc-bg)]">
              <div className="flex flex-col gap-6">
                {data.reportType === ReportType.CENTRAL_MONITORING ? (
                  /* ── Central Monitoring layout ── */
                  <>
                    {/* CM Title block */}
                    <div className="rounded-[10px] border border-[var(--sc-border)] bg-[var(--sc-surface)] px-6 py-5">
                      {/* CM: bez typu i numeru wizyty, edytowana cała nazwa */}
                      {isEditingName ? (
                        <input
                          ref={nameInputRef}
                          value={editedName}
                          onChange={(e) => setEditedName(e.target.value)}
                          onBlur={commitName}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") commitName();
                            if (e.key === "Escape") setIsEditingName(false);
                          }}
                          className="w-full text-[20px] font-bold text-[var(--sc-text)] leading-snug bg-transparent border-b border-[var(--sc-border)] outline-none focus:border-[#F4A832] pb-0.5"
                          autoFocus
                        />
                      ) : (
                        <h1
                          className="text-[20px] font-bold text-[var(--sc-text)] leading-snug cursor-text hover:text-[var(--sc-muted)] transition-colors group flex items-center gap-2"
                          onClick={() => startEditingName(data.name)}
                          title="Kliknij, aby zmienić nazwę"
                        >
                          {data.name}
                          <span className="text-[13px] font-normal text-[var(--sc-muted)] opacity-0 group-hover:opacity-100 transition-opacity">✎</span>
                        </h1>
                      )}
                      {metadata && (
                        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-[13px] text-[var(--sc-muted)]">
                          {(metadata.visitDate || metadata.visitDateEnd) && (
                            <span>
                              <span className="font-medium text-[var(--sc-text)]">Okres: </span>
                              {metadata.visitDate
                                ? new Date(metadata.visitDate).toLocaleDateString("pl-PL", { day: "numeric", month: "numeric", year: "numeric" })
                                : "—"}
                              {" – "}
                              {metadata.visitDateEnd
                                ? new Date(metadata.visitDateEnd).toLocaleDateString("pl-PL", { day: "numeric", month: "numeric", year: "numeric" })
                                : "—"}
                            </span>
                          )}
                          <span>
                            <span className="font-medium text-[var(--sc-text)]">Badanie: </span>
                            {data.study}
                          </span>
                        </div>
                      )}
                    </div>

                    <CmTrackingDates
                      reportId={data.id}
                      cmAnalysisDate={data.cmAnalysisDate ?? null}
                      cmReportDate={data.cmReportDate ?? null}
                      cmPmVerificationDate={data.cmPmVerificationDate ?? null}
                      sentToSponsorDate={data.sentToSponsorDate ?? null}
                      tmfDate={data.tmfDate ?? null}
                    />

                    {/* Verified sites – above questions */}
                    <div className="rounded-[10px] border border-[var(--sc-border)] bg-[var(--sc-surface)] px-6 py-4">
                      <p className="text-[11px] font-medium text-[var(--sc-muted)] mb-2">
                        Zweryfikowano dane dla Ośrodków
                      </p>
                      <p className="text-[13px] text-[var(--sc-text)]">
                        {metadata?.croParticipants || <span className="text-[var(--sc-muted)]">—</span>}
                      </p>
                      {metadata && (
                        <button
                          onClick={() => setIsMetadataModalOpen(true)}
                          className="mt-2 text-[11px] text-[#F4A832] hover:underline"
                        >
                          Edytuj
                        </button>
                      )}
                    </div>

                    {/* Questions – flat numbering, no section header */}
                    {sections && (
                      <SectionsList
                        sections={sections}
                        templateId={data.templateId}
                        reportId={reportId}
                        isCentralMonitoring
                      />
                    )}

                    {/* Final comment */}
                    {metadata && (
                      <FinalComment
                        metadata={metadata}
                        onOpenMetadataModal={() => setIsMetadataModalOpen(true)}
                      />
                    )}
                    <div className="rounded-[10px] border border-[var(--sc-border)] bg-[var(--sc-surface)] px-6 py-4">
                      <ReportFiles files={reportFiles} reportId={reportId} />
                    </div>
                  </>
                ) : (
                  /* ── Standard report layout ── */
                  <>
                  {/* Report title: typ i numer wizyty (nazwa = typ + numer, jak na liście) */}
                  <div className="rounded-[10px] border border-[var(--sc-border)] bg-[var(--sc-surface)] px-6 py-4">
                    <VisitTitleEditor
                      reportId={data.id}
                      studyId={data.studyId}
                      name={data.name}
                      visitName={data.visitName}
                      visitNumber={data.visitNumber}
                    />
                  </div>

                  {/* Visit date missing banner */}
                  {metadata && !metadata.visitDate && (
                    <div className="flex items-center justify-between gap-3 rounded-[10px] border border-[#F5D98A] bg-[#FFFBEB] px-5 py-3">
                      <div className="flex items-center gap-2 text-[13px] text-[#92620A]">
                        <span>⚠️</span>
                        <span>Data wizyty nie jest ustawiona — raport nie pojawi się w kalendarzu.</span>
                      </div>
                      <button
                        onClick={() => setIsMetadataModalOpen(true)}
                        className="flex-shrink-0 rounded-full px-3 py-1 text-[12px] font-semibold bg-[#F4A832] text-[#0F0F0F] hover:bg-[#e09920] transition-colors"
                      >
                        Uzupełnij →
                      </button>
                    </div>
                  )}

                {reportId && <ReportStateTimeline reportId={reportId} />}

                <ReportTrackingDates
                  reportId={data.id}
                  sentToSponsorDate={data.sentToSponsorDate ?? null}
                  tmfDate={data.tmfDate ?? null}
                />

                {metadata && (
                  <div className="rounded-[10px] border border-[var(--sc-border)] bg-[var(--sc-surface)] overflow-hidden">
                    {/* Card header */}
                    <button
                      type="button"
                      className="w-full flex items-center justify-between px-5 py-3 border-b border-[var(--sc-border)] text-left"
                      onClick={() => setIsMetadataExpanded((v) => !v)}
                    >
                      <span className="flex items-center gap-1.5">
                        <MdExpandMore
                          size={18}
                          className="text-[var(--sc-muted)] transition-transform duration-200"
                          style={{ transform: isMetadataExpanded ? "rotate(180deg)" : "rotate(0deg)" }}
                        />
                        <span className="text-[15px] font-semibold text-[var(--sc-text)]">Dane raportu</span>
                        <span className="text-[11px] text-[var(--sc-muted)]">
                          {isMetadataExpanded ? "ukryj" : "pokaż"}
                        </span>
                      </span>
                      <Button variant="secondary" size="sm" onClick={(e) => { e.stopPropagation(); setIsMetadataModalOpen(true); }}>
                        Edytuj
                      </Button>
                    </button>

                    {isMetadataExpanded && (
                      <div className="divide-y divide-[var(--sc-border)]">
                        {/* Protokół */}
                        <div className="px-5 py-4">
                          <p className="text-[11px] font-medium text-[var(--sc-muted)] mb-3">Protokół</p>
                          <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
                            {([
                              { label: "Nr protokołu",  value: metadata.protocolNumber },
                              { label: "Akronim",        value: metadata.studyAcronym },
                              { label: "Nr ośrodka",     value: metadata.resortNumber },
                              { label: "Gł. badacz",     value: metadata.mainResearcher },
                            ] as { label: string; value?: string | null }[]).map(({ label, value }) => (
                              <div key={label}>
                                <p className="text-[11px] font-medium text-[var(--sc-muted)] mb-0.5">{label}</p>
                                <p className="text-[13px] text-[var(--sc-text)]">{value || "—"}</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Wizyta */}
                        <div className="px-5 py-4">
                          <p className="text-[11px] font-medium text-[var(--sc-muted)] mb-3">Wizyta</p>
                          <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
                            {([
                              {
                                label: "Data wizyty",
                                value: metadata.visitDate
                                  ? isMultiDayVisit(metadata.visitDate, metadata.visitDateEnd)
                                    ? `${new Date(metadata.visitDate).toLocaleDateString("pl-PL", { day: "numeric", month: "short" })} – ${new Date(metadata.visitDateEnd).toLocaleDateString("pl-PL", { day: "numeric", month: "short", year: "numeric" })}`
                                    : new Date(metadata.visitDate).toLocaleDateString("pl-PL", { day: "numeric", month: "long", year: "numeric" })
                                  : undefined,
                              },
                              { label: "Numer wizyty", value: data.name },
                            ] as { label: string; value?: string | null }[]).map(({ label, value }) => (
                              <div key={label}>
                                <p className="text-[11px] font-medium text-[var(--sc-muted)] mb-0.5">{label}</p>
                                <p className="text-[13px] text-[var(--sc-text)]">{value || "—"}</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Uczestnicy – liczby */}
                        <div className="px-5 py-4">
                          <p className="text-[11px] font-medium text-[var(--sc-muted)] mb-3">Uczestnicy</p>
                          <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
                            {([
                              { label: "Zadeklarowani",            value: metadata.declaredParticipants },
                              { label: "Skriningowani",            value: metadata.afterScreening },
                              { label: "Zrandomizowani",           value: metadata.randomized },
                              { label: "Niepow. skriningu",        value: metadata.screenFailure },
                              { label: "W trakcie leczenia",       value: metadata.duringTreatment },
                              { label: "W trakcie obserwacji",     value: metadata.duringFollowUp },
                              { label: "EOT (przedwczesne)",       value: metadata.prematureCompletion },
                              { label: "EOS (zakończony)",         value: metadata.completedStudy },
                            ] as { label: string; value?: number | null }[]).map(({ label, value }) => (
                              <div key={label}>
                                <p className="text-[11px] font-medium text-[var(--sc-muted)] mb-0.5">{label}</p>
                                <p className="text-[13px] text-[var(--sc-text)]">{value ?? "—"}</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Status badania */}
                        <div className="px-5 py-4">
                          <p className="text-[11px] font-medium text-[var(--sc-muted)] mb-3">Status badania</p>
                          <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
                            {([
                              { label: "Rekrutacja",              value: metadata.recrutationStatus != null ? `${metadata.recrutationStatus}%` : undefined },
                              { label: "Odchylenia od protokołu", value: metadata.protocolDeviation },
                              { label: "SAE",                     value: metadata.reportedSAE },
                            ] as { label: string; value?: string | number | null }[]).map(({ label, value }) => (
                              <div key={label}>
                                <p className="text-[11px] font-medium text-[var(--sc-muted)] mb-0.5">{label}</p>
                                <p className="text-[13px] text-[var(--sc-text)]">{value ?? "—"}</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Uczestnicy – tekst */}
                        {(metadata.croParticipants || metadata.resortParticipants) && (
                          <div className="px-5 py-4">
                            <p className="text-[11px] font-medium text-[var(--sc-muted)] mb-3">Uczestnicy – tekstowo</p>
                            <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                              {([
                                { label: "Uczestnicy CRO",    value: metadata.croParticipants },
                                { label: "Uczestnicy ośrodka", value: metadata.resortParticipants },
                              ] as { label: string; value?: string | null }[]).map(({ label, value }) => (
                                <div key={label} className="min-w-0">
                                  <p className="text-[11px] font-medium text-[var(--sc-muted)] mb-0.5">{label}</p>
                                  <p className="text-[13px] text-[var(--sc-text)] whitespace-pre-wrap break-words">{value || "—"}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {participants && (
                  <ParticipantsSectionsList items={participants} reportId={reportId} />
                )}
                {problems && (
                  <ProblemsSection
                    templateId={data.templateId}
                    items={problems.filter((x) => x.problemType === ProblemType.MAJOR)}
                    reportId={reportId}
                    problemType={ProblemType.MAJOR}
                  />
                )}
                {problems && (
                  <ProblemsSection
                    templateId={data.templateId}
                    items={problems.filter((x) => x.problemType === ProblemType.MINOR)}
                    reportId={reportId}
                    problemType={ProblemType.MINOR}
                  />
                )}
                {actions && (
                  <ActionLogGrid
                    reportId={reportId}
                    actions={actions}
                    isLoading={actionsLoading}
                    error={actionsError}
                  />
                )}
                {SiteIssuesInReport && (
                  <ReportSiteIssuesSection reportId={reportId!} reportState={data.state} />
                )}
                {sections && (
                  <SectionsList
                    sections={sections}
                    templateId={data.templateId}
                    reportId={reportId}
                  />
                )}
                {metadata && (
                  <FinalComment
                    metadata={metadata}
                    onOpenMetadataModal={() => setIsMetadataModalOpen(true)}
                  />
                )}
                <div className="rounded-[10px] border border-[var(--sc-border)] bg-[var(--sc-surface)] px-6 py-4">
                  <ReportFiles files={reportFiles} reportId={reportId} />
                </div>
                  </>
                )}
              </div>
            </main>
          </div>
        </div>
      ) : null}
      {metadata && (
        <MetadataEditModal
          isOpen={isMetadataModalOpen}
          onClose={() => setIsMetadataModalOpen(false)}
          metadata={metadata}
          reportId={reportId!}
          isDisabled={false}
          isCentralMonitoring={data?.reportType === ReportType.CENTRAL_MONITORING}
        />
      )}
    </>
  );
}

export const ReportPageWrapper = () => {
  return <ReportPage />;
};
